import I18n from '~/utils/translations';
import URLSearchParamUtils from '~/utils/url_search_param_utils';
import { joinClasses } from 'components/utils/react_helper';
import { createBrowserHistory } from 'history';
import moment from 'moment';
import React, { Component, Fragment } from 'react';

import ChevronWhite from '../../images/chevron-down-white.svg';
import Chevron from '../../images/chevron-down.svg';
import Globe from '../../images/homes/city-icon-everywhere.svg';
// Icons
import Furniture from '../../images/homes/furniture.svg';
import Furnished from '../../images/homes/price-icon-furnished.svg';
import Unfurnished from '../../images/homes/price-icon-unfurnished.svg';
import GBFlag from '../../images/locale-flag-uk.png';
import USFlag from '../../images/locale-flag-us.png';
import BedroomCountRange from './bedroom_count_range';
import LocationButtons from './location_buttons';
import MonthlyRentDropdown from './monthly_rent_dropdown';
import PreferredDateFilter from './preferred_date_filter';

export default class FilterPreferences extends Component {
  constructor(props) {
    super(props);

    const today = moment().toDate();

    this.state = {
      open: false,
      text: '',
      city: this.props.initialCity,
      minRent: null,
      maxRent: null,
      minBeds: null,
      maxBeds: null,
      preferredMoveInDate: today,
    };

    this.history = createBrowserHistory();
    this.location = this.history.location;
    this.unlisten = this.history.listen(this.setFiltersFromURL);

    this.options = {
      location: this.generateLocationOptions(),
      furnishing: [
        {
          value: 'furnished',
          text: I18n.t('pages.homes.price_preferences.furnished'),
          icon: Furniture,
          secondaryIcon: Furnished,
          svg: true,
        },
        {
          value: 'unfurnished',
          text: I18n.t('pages.homes.price_preferences.unfurnished'),
          icon: Furniture,
          secondaryIcon: Unfurnished,
          svg: true,
        },
      ],
    };
  }

  get classes() {
    return joinClasses(['filter-preferences', this.state.open && 'filter-preferences--open']);
  }

  get buttonClasses() {
    return joinClasses(['filter-preferences-button filter-preferences-button--primary']);
  }

  render() {
    return (
      <div className={this.classes}>
        <PreferredDateFilter
          onDateChange={this.updateFilterOption('preferredMoveInDate')}
          selectedDate={this.state.preferredMoveInDate ? this.state.preferredMoveInDate : ''}
          isFilterOpen={this.state.open}
          onFilterChange={this.toggleFilterPane}
        />
        <a className={this.buttonClasses} onClick={this.toggleFilterPane}>
          <div className="filter-preferences-button__title-container">
            <span className="filter-preferences-button__status">
              {this.props.enableCompareFeature ? 'Filters ' : this.buildSelectedDisplay()}
            </span>
          </div>
          <img
            className="filter-preferences-button__arrow"
            src={this.props.enableCompareFeature ? ChevronWhite : Chevron}
          />
        </a>
        <div className="filter-options">
          {this.props.enableLocationFilter && (
            <LocationButtons
              options={this.options.location}
              locationFilter={this.state.city}
              onLocationFilterChange={this.updateCity}
            />
          )}
          {this.state.city !== 'everywhere' && (
            <MonthlyRentDropdown
              city={this.state.city}
              minRent={this.state.minRent}
              onMinRentChange={this.updateFilterOption('minRent')}
              maxRent={this.state.maxRent}
              onMaxRentChange={this.updateFilterOption('maxRent')}
            />
          )}
          <BedroomCountRange
            minBeds={this.state.minBeds}
            onMinBedsChange={this.updateFilterOption('minBeds')}
            maxBeds={this.state.maxBeds}
            onMaxBedsChange={this.updateFilterOption('maxBeds')}
          />
        </div>
      </div>
    );
  }

  componentDidMount() {
    this.setFiltersFromURL();
  }

  // The filters whose state we want to store
  // in the URL
  get urlFilters() {
    const filtersToStore = ['moveInDate', 'minRent', 'maxRent', 'minBeds', 'maxBeds'];

    if (this.props.enableLocationFilter) {
      filtersToStore.unshift('city');
    }

    return filtersToStore;
  }

  // Utility functions to get filter params from the URL
  // Can probably hide these away somewhere like the logic for
  // saving into the URL
  get city() {
    if (!this.props.enableLocationFilter) {
      return this.state.city;
    }

    return URLSearchParamUtils.paramPresent('city') !== false
      ? URLSearchParamUtils.getParamFromURL('city')
      : this.state.city;
  }

  get rentRange() {
    return URLSearchParamUtils.paramPresent('minRent') || URLSearchParamUtils.paramPresent('maxRent')
      ? [URLSearchParamUtils.getParamFromURL('minRent'), URLSearchParamUtils.getParamFromURL('maxRent')]
      : [null, null];
  }

  get bedsRange() {
    return URLSearchParamUtils.paramPresent('minBeds') || URLSearchParamUtils.paramPresent('maxBeds')
      ? [URLSearchParamUtils.getParamFromURL('minBeds'), URLSearchParamUtils.getParamFromURL('maxBeds')]
      : [null, null];
  }

  get moveInDate() {
    return URLSearchParamUtils.paramPresent('moveInDate') !== false
      ? moment(URLSearchParamUtils.getParamFromURL('moveInDate')).toDate()
      : this.state.preferredMoveInDate;
  }

  checkAvailabilityFunction(date) {
    return (d) => moment(d).isSameOrBefore(date);
  }

  onPreferredDateChange = (date) => {
    this.setState({ preferredMoveInDate: date }, () => {
      this.props.updateFilter(this.isListingIncluded);
      this.storeParamsInURL();
    });
  };

  setFiltersFromURL = () => {
    const [minRent, maxRent] = this.rentRange;
    const [minBeds, maxBeds] = this.bedsRange;

    this.setState(
      {
        city: this.city,
        minRent,
        maxRent,
        minBeds,
        maxBeds,
        preferredMoveInDate: this.moveInDate,
      },
      () => {
        this.props.updateFilter(this.isListingIncluded);
      }
    );
  };

  storeParamsInURL = () => {
    const stateToStore = this.urlFilters.reduce((acc, val) => {
      if (val === 'moveInDate') {
        const selectedDate = moment(this.state.preferredMoveInDate);
        if (!moment().isSame(selectedDate, 'day')) {
          acc[val] = this.state.preferredMoveInDate ? selectedDate.format('YYYY-MM-DD') : '';
        } else {
          acc[val] = '';
        }
      } else {
        acc[val] = this.state[val];
      }

      return acc;
    }, {});

    this.history.push(URLSearchParamUtils.updateURLParams(stateToStore));
  };

  // Filtering functions for specific values
  checkIfPriceInRange = (price) => {
    if (price == null) {
      return false;
    }

    const { minRent, maxRent } = this.state;

    return (!minRent || price / 100 >= minRent) && (!maxRent || price / 100 <= maxRent);
  };

  checkIfBedsInRange = (beds) => {
    const { minBeds, maxBeds } = this.state;
    return beds != null && (!minBeds || beds >= minBeds) && (!maxBeds || beds <= maxBeds);
  };

  // Main filtering function. This is passed up into the collection where it is used
  // as the decider of what should or should not be shown to the user.
  isListingIncluded = (listing) => {
    const { city, preferredMoveInDate } = this.state;
    const isSameLocale = city === 'everywhere' || listing.locale === city;
    const isWithinRentRange = this.checkIfPriceInRange(listing.priceInPence);
    const isWithinBedsRange = this.checkIfBedsInRange(listing.bedrooms);
    const isWithinDateRange =
      !preferredMoveInDate || this.isWithinDateRange(listing.availableFrom, preferredMoveInDate, 30);

    return [isSameLocale, isWithinRentRange, isWithinBedsRange, isWithinDateRange].every(Boolean);
  };

  isWithinDateRange = (availableFromDate, preferredMoveInDate, days) => {
    const after = moment(preferredMoveInDate).subtract(days, 'days').toDate();
    const before = moment(preferredMoveInDate).add(days, 'days').toDate();

    const datesInRange = [moment(availableFromDate), moment()].filter((d) => d.isValid() && d.isBetween(after, before));
    return datesInRange.length > 0;
  };

  // Generic state updater for values updated via a select. Handles triggering useful
  // GA events, setting the state, storing the updated filter values in the URL and
  // passing the new filter function up into the collection.
  updateFilterOption = (stateKey) => (stateValue) => {
    track_event('price-preferences', `filtered_${stateKey}`, null, stateValue);

    if (stateValue == 'none') {
      stateValue = null;
    }

    this.setState(
      {
        [stateKey]: stateValue,
      },
      () => {
        this.storeParamsInURL();
        this.props.updateFilter(this.isListingIncluded);
      }
    );
  };

  generateLocationOptions = () => {
    const everywhereButton = {
      value: 'everywhere',
      text: I18n.t('pages.homes.location_preferences.everywhere'),
      icon: Globe,
      svg: true,
    };

    return [
      ...(this.props.enableCompareFeature ? [] : [everywhereButton]),
      {
        value: 'new-york',
        text: I18n.t('pages.homes.location_preferences.new-york'),
        icon: USFlag,
      },
      {
        value: 'london',
        text: I18n.t('pages.homes.location_preferences.london'),
        icon: GBFlag,
      },
    ];
  };

  updateCity = (location) => {
    this.setState(
      {
        city: location,
      },
      () => {
        this.storeParamsInURL();
        this.buildSelectedDisplay();
        this.props.updateFilter(this.isListingIncluded);
      }
    );
  };

  updateSelectedPrice = (selectedPriceOption) => {
    this.props.onPriceFilterChange(selectedPriceOption);
  };

  toggleFilterPane = () => {
    const newOpenState = !this.state.open;
    track_event('price-preferences', newOpenState ? 'open' : 'close');
    this.setState({ open: newOpenState });
  };

  // Display/Rendering functions
  buildText = (preferences) => {
    let furnishing = I18n.t(`pages.homes.price_preferences.${preferences.furnishing}`);
    return `${furnishing}`;
  };

  buildSelectedDisplay = () => {
    const furnishingOption = this.options.furnishing.find((o) => o.value == this.props.priceFilter);
    const locationOption = this.options.location.find((o) => o.value == this.state.city);

    return (
      <Fragment>
        <LocationDisplayOption {...locationOption} />
        <DisplayOption {...furnishingOption} />
      </Fragment>
    );
  };
}

function DisplayOptionWrapper({ children, value }) {
  return (
    <div key={value} className="selected-option-display">
      {children}
    </div>
  );
}

function DisplayOption({ value, icon, secondaryIcon }) {
  return (
    <DisplayOptionWrapper value={value}>
      <div className="preferences-pane__icon">
        <img className="preferences-pane__icon-image" src={icon} />
        <img className="preferences-pane__icon-image--secondary" src={secondaryIcon} />
      </div>
    </DisplayOptionWrapper>
  );
}

function LocationDisplayOption({ value, text, icon }) {
  return (
    <DisplayOptionWrapper value={value}>
      <div className="preferences-pane__icon">
        <img className="preferences-pane__icon-image" src={icon} />
      </div>
      <span className="preferences-pane__text">{text}</span>
    </DisplayOptionWrapper>
  );
}
